import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { useMusicPlayer } from './music-player-context';
import { apiUrl } from '@/config/api';

export interface PlaybackSegment {
  type: 'dj_intro' | 'track_intro' | 'music_preview' | 'dj_commentary' | 'dj_transition' | 'dj_outro';
  audioUrl?: string;
  previewUrl?: string;
  durationMs: number;
  text?: string;
  submissionId?: number;
  title?: string;
  artistName?: string;
  albumName?: string;
  thumbnailUrl?: string;
  submitterUsername?: string;
  submitterAvatarUrl?: string;
  totalPoints?: number;
  voteCount?: number;
  streamingLinks?: Record<string, string>;
  previewExpired?: boolean;
}

export interface RadioEpisode {
  roundId: number;
  roundName: string;
  roundDescription?: string;
  roundNumber?: number;
  shareToken?: string;
  leagueName: string;
  leagueCategory?: string;
  leagueThumbnailUrl?: string;
  leagueHeaderImageUrl?: string;
  leagueThemeColor?: string;
  segments: PlaybackSegment[];
  totalDurationMs: number;
  trackCount: number;
}

export interface EpisodeListItem {
  roundId: number;
  roundName: string;
  leagueName: string;
}

export type PlayerState = 'idle' | 'loading' | 'playing' | 'paused';

export interface NowPlaying {
  type: PlaybackSegment['type'];
  title?: string;
  artistName?: string;
  albumName?: string;
  thumbnailUrl?: string;
  submitterUsername?: string;
  totalPoints?: number;
  voteCount?: number;
  streamingLinks?: Record<string, string>;
  submissionId?: number;
  djText?: string;
}

const MAX_SKIPS = 3;

interface RadioPlaybackContextType {
  episode: RadioEpisode | null;
  playerState: PlayerState;
  currentSegmentIndex: number;
  segmentProgress: number;
  overallProgress: number;
  skipsRemaining: number;
  nowPlaying: NowPlaying | null;
  currentTrack: PlaybackSegment | undefined;
  volume: number;
  // Episode controls
  loadEpisode: (ep: RadioEpisode) => void;
  play: () => void;
  pause: () => void;
  resume: () => void;
  togglePlayPause: () => void;
  skip: () => void;
  stop: () => void;
  setVolume: (v: number) => void;
  // Episode playlist controls
  episodePlaylist: EpisodeListItem[] | null;
  currentEpisodeIndex: number;
  setEpisodePlaylist: (list: EpisodeListItem[], startIndex: number) => void;
  nextEpisode: () => void;
  prevEpisode: () => void;
  hasNextEpisode: boolean;
  hasPrevEpisode: boolean;
  // Audio element for lip sync
  audioElement: HTMLAudioElement | null;
}

const RadioPlaybackContext = createContext<RadioPlaybackContextType | null>(null);

export function RadioPlaybackProvider({ children }: { children: ReactNode }) {
  const [episode, setEpisode] = useState<RadioEpisode | null>(null);
  const [playerState, setPlayerState] = useState<PlayerState>('idle');
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [segmentProgress, setSegmentProgress] = useState(0);
  const [skipsRemaining, setSkipsRemaining] = useState(MAX_SKIPS);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  const [volume, setVolumeState] = useState(0.8);

  // Episode playlist state
  const [episodePlaylist, setEpisodePlaylistState] = useState<EpisodeListItem[] | null>(null);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const nextAudioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const episodeRef = useRef<RadioEpisode | null>(null);
  const currentIndexRef = useRef(0);
  const volumeRef = useRef(0.8);
  const episodePlaylistRef = useRef<EpisodeListItem[] | null>(null);
  const currentEpisodeIndexRef = useRef(0);
  const onEpisodeCompleteRef = useRef<(() => void) | null>(null);

  // Keep refs in sync with state (for use in callbacks/closures)
  episodeRef.current = episode;
  currentIndexRef.current = currentSegmentIndex;
  volumeRef.current = volume;
  episodePlaylistRef.current = episodePlaylist;
  currentEpisodeIndexRef.current = currentEpisodeIndex;

  // Access music player context for radio mode integration
  const { activateRadio, updateRadioTrack, clearRadio, setIsPlaying: setContextPlaying } = useMusicPlayer();

  // Get the audio URL for a segment
  const getSegmentAudioUrl = (seg: PlaybackSegment): string | null => {
    if (seg.type === 'music_preview') {
      return seg.previewExpired ? null : (seg.previewUrl || null);
    }
    return seg.audioUrl || null;
  };

  // Update now playing info based on current segment
  const updateNowPlaying = useCallback((seg: PlaybackSegment) => {
    if (seg.type === 'music_preview') {
      setNowPlaying({
        type: seg.type,
        title: seg.title,
        artistName: seg.artistName,
        albumName: seg.albumName,
        thumbnailUrl: seg.thumbnailUrl,
        submitterUsername: seg.submitterUsername,
        totalPoints: seg.totalPoints,
        voteCount: seg.voteCount,
        streamingLinks: seg.streamingLinks,
        submissionId: seg.submissionId,
      });
    } else {
      setNowPlaying(prev => ({
        ...prev,
        type: seg.type,
        djText: seg.text,
        submissionId: seg.submissionId,
      }));
    }
  }, []);

  // Update MediaSession API for lock screen / OS controls
  const updateMediaSession = useCallback((seg: PlaybackSegment) => {
    if (!('mediaSession' in navigator)) return;

    const isDjSegment = seg.type !== 'music_preview';
    navigator.mediaSession.metadata = new MediaMetadata({
      title: isDjSegment ? 'DJ Zap' : (seg.title || 'Unknown Track'),
      artist: isDjSegment ? 'YapZap Radio' : (seg.artistName || ''),
      album: isDjSegment ? episodeRef.current?.roundName : (seg.albumName || ''),
      artwork: seg.thumbnailUrl
        ? [{ src: seg.thumbnailUrl, sizes: '512x512', type: 'image/jpeg' }]
        : [],
    });
  }, []);

  // Sync track info to mini player context
  const syncToMiniPlayer = useCallback((seg: PlaybackSegment) => {
    if (seg.type === 'music_preview' || seg.thumbnailUrl) {
      updateRadioTrack({
        id: `radio-track-${seg.submissionId || Date.now()}`,
        title: seg.title || episodeRef.current?.roundName || 'YapZap Radio',
        artistName: seg.artistName || 'DJ Zap',
        thumbnailUrl: seg.thumbnailUrl,
      });
    }
  }, [updateRadioTrack]);

  // Preload the next segment for gapless playback
  const preloadNext = useCallback((nextIndex: number) => {
    const ep = episodeRef.current;
    if (!ep || nextIndex >= ep.segments.length) return;

    const nextSeg = ep.segments[nextIndex];
    const nextUrl = getSegmentAudioUrl(nextSeg);
    if (!nextUrl) return;

    if (!nextAudioRef.current) {
      nextAudioRef.current = new Audio();
    }
    nextAudioRef.current.src = nextUrl;
    nextAudioRef.current.preload = 'auto';
  }, []);

  // Play a specific segment by index
  const playSegment = useCallback((index: number) => {
    const ep = episodeRef.current;
    if (!ep || index >= ep.segments.length) {
      // Episode finished — try auto-advance to next episode
      setPlayerState('idle');
      setCurrentSegmentIndex(0);
      setSegmentProgress(0);
      setContextPlaying(false);
      if (onEpisodeCompleteRef.current) {
        onEpisodeCompleteRef.current();
      }
      return;
    }

    const seg = ep.segments[index];
    const audioUrl = getSegmentAudioUrl(seg);

    setCurrentSegmentIndex(index);
    currentIndexRef.current = index;
    setSegmentProgress(0);
    updateNowPlaying(seg);
    updateMediaSession(seg);
    syncToMiniPlayer(seg);

    if (!audioUrl) {
      if (seg.type === 'music_preview') {
        // Skip expired previews immediately
        playSegment(index + 1);
        return;
      }
      // For text-only DJ segments, show text briefly then advance
      setPlayerState('playing');
      const textDuration = Math.max(2000, Math.min(seg.text?.length || 0, 200) * 40);
      setTimeout(() => playSegment(index + 1), textDuration);
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    const audio = audioRef.current;
    audio.volume = volumeRef.current;
    audio.src = audioUrl;

    // Progress tracking
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    progressIntervalRef.current = window.setInterval(() => {
      if (audio.duration > 0) {
        setSegmentProgress(audio.currentTime / audio.duration);
      }
    }, 100);

    // Auto-advance when segment ends
    audio.onended = () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      playSegment(index + 1);
    };

    audio.onerror = () => {
      console.warn(`📻 Audio error on segment ${index}, skipping`);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      playSegment(index + 1);
    };

    setPlayerState('loading');
    audio.play()
      .then(() => {
        setPlayerState('playing');
        setContextPlaying(true);
      })
      .catch(err => {
        console.warn('📻 Playback failed:', err.message);
        playSegment(index + 1);
      });

    preloadNext(index + 1);
  }, [updateNowPlaying, updateMediaSession, syncToMiniPlayer, preloadNext, setContextPlaying]);

  // Set up MediaSession action handlers
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.setActionHandler('play', () => resumePlayback());
    navigator.mediaSession.setActionHandler('pause', () => pausePlayback());
    navigator.mediaSession.setActionHandler('nexttrack', () => skipTrack());
  }, []);

  // Public API
  const loadEpisode = useCallback((ep: RadioEpisode) => {
    // Stop current playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    setEpisode(ep);
    episodeRef.current = ep;
    setCurrentSegmentIndex(0);
    currentIndexRef.current = 0;
    setSegmentProgress(0);
    setPlayerState('idle');
    setSkipsRemaining(MAX_SKIPS);
    setNowPlaying(null);

    // Activate radio mode in the global music player context
    activateRadio(ep.roundId, ep.roundName, ep.leagueName);
  }, [activateRadio]);

  const startPlayback = useCallback(() => {
    if (!episodeRef.current) return;
    playSegment(currentIndexRef.current);
  }, [playSegment]);

  const pausePlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    setPlayerState('paused');
    setContextPlaying(false);
  }, [setContextPlaying]);

  const resumePlayback = useCallback(() => {
    if (audioRef.current && audioRef.current.src) {
      audioRef.current.play()
        .then(() => {
          setPlayerState('playing');
          setContextPlaying(true);
        })
        .catch(() => {});

      progressIntervalRef.current = window.setInterval(() => {
        if (audioRef.current && audioRef.current.duration > 0) {
          setSegmentProgress(audioRef.current.currentTime / audioRef.current.duration);
        }
      }, 100);
    }
  }, [setContextPlaying]);

  const togglePlayPause = useCallback(() => {
    if (playerState === 'playing') {
      pausePlayback();
    } else if (playerState === 'paused') {
      resumePlayback();
    } else {
      startPlayback();
    }
  }, [playerState, startPlayback, pausePlayback, resumePlayback]);

  const skipTrack = useCallback(() => {
    const ep = episodeRef.current;
    if (!ep) return;

    let nextIndex = currentIndexRef.current + 1;
    while (nextIndex < ep.segments.length) {
      if (ep.segments[nextIndex].type === 'music_preview') {
        if (nextIndex > 0 && ep.segments[nextIndex - 1].type === 'track_intro') {
          playSegment(nextIndex - 1);
          return;
        }
        playSegment(nextIndex);
        return;
      }
      nextIndex++;
    }
    playSegment(currentIndexRef.current + 1);
  }, [playSegment]);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    setEpisode(null);
    episodeRef.current = null;
    setPlayerState('idle');
    setCurrentSegmentIndex(0);
    setSegmentProgress(0);
    setNowPlaying(null);
    setEpisodePlaylistState(null);
    episodePlaylistRef.current = null;
    clearRadio();
    setContextPlaying(false);
  }, [clearRadio, setContextPlaying]);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    volumeRef.current = v;
    if (audioRef.current) {
      audioRef.current.volume = v;
    }
  }, []);

  // === Episode playlist management ===

  const setEpisodePlaylist = useCallback((list: EpisodeListItem[], startIndex: number) => {
    setEpisodePlaylistState(list);
    episodePlaylistRef.current = list;
    setCurrentEpisodeIndex(startIndex);
    currentEpisodeIndexRef.current = startIndex;
  }, []);

  // Fetch and load a specific episode by roundId (used for next/prev)
  const fetchAndLoadEpisode = useCallback(async (roundId: number) => {
    try {
      setPlayerState('loading');
      const response = await fetch(apiUrl(`/api/radio/episodes/${roundId}`));
      const data = await response.json();
      if (data.segments) {
        const ep: RadioEpisode = {
          roundId: data.roundId,
          roundName: data.roundName || `Round ${data.roundNumber}`,
          roundDescription: data.roundDescription,
          roundNumber: data.roundNumber,
          shareToken: data.shareToken,
          leagueName: data.leagueName,
          leagueCategory: data.leagueCategory,
          leagueThumbnailUrl: data.leagueThumbnailUrl,
          leagueHeaderImageUrl: data.leagueHeaderImageUrl,
          leagueThemeColor: data.leagueThemeColor,
          segments: data.segments,
          totalDurationMs: data.totalDurationMs,
          trackCount: data.trackCount,
        };
        loadEpisode(ep);
        // Auto-start playback
        setTimeout(() => playSegment(0), 100);
      }
    } catch (err) {
      console.error('📻 Failed to fetch episode:', err);
      setPlayerState('idle');
    }
  }, [loadEpisode, playSegment]);

  const nextEpisode = useCallback(() => {
    const playlist = episodePlaylistRef.current;
    if (!playlist || playlist.length === 0) return;
    const nextIdx = (currentEpisodeIndexRef.current + 1) % playlist.length;
    setCurrentEpisodeIndex(nextIdx);
    currentEpisodeIndexRef.current = nextIdx;
    fetchAndLoadEpisode(playlist[nextIdx].roundId);
  }, [fetchAndLoadEpisode]);

  const prevEpisode = useCallback(() => {
    const playlist = episodePlaylistRef.current;
    if (!playlist || playlist.length === 0) return;
    const prevIdx = (currentEpisodeIndexRef.current - 1 + playlist.length) % playlist.length;
    setCurrentEpisodeIndex(prevIdx);
    currentEpisodeIndexRef.current = prevIdx;
    fetchAndLoadEpisode(playlist[prevIdx].roundId);
  }, [fetchAndLoadEpisode]);

  // Set up auto-advance callback
  onEpisodeCompleteRef.current = episodePlaylist && episodePlaylist.length > 1 ? nextEpisode : null;

  // Compute overall progress
  const overallProgress = episode
    ? episode.segments.slice(0, currentSegmentIndex).reduce((sum, s) => sum + s.durationMs, 0) / episode.totalDurationMs
    : 0;

  // Get the current music track info
  const currentTrack = episode?.segments.find(
    (s, i) => s.type === 'music_preview' && i <= currentSegmentIndex && i >= currentSegmentIndex - 2
  ) || episode?.segments.find(s => s.type === 'music_preview');

  const hasNextEpisode = !!episodePlaylist && episodePlaylist.length > 1;
  const hasPrevEpisode = !!episodePlaylist && episodePlaylist.length > 1;

  const contextValue: RadioPlaybackContextType = {
    episode,
    playerState,
    currentSegmentIndex,
    segmentProgress,
    overallProgress,
    skipsRemaining,
    nowPlaying,
    currentTrack,
    volume,
    loadEpisode,
    play: startPlayback,
    pause: pausePlayback,
    resume: resumePlayback,
    togglePlayPause,
    skip: skipTrack,
    stop: stopPlayback,
    setVolume,
    // Episode playlist
    episodePlaylist,
    currentEpisodeIndex,
    setEpisodePlaylist,
    nextEpisode,
    prevEpisode,
    hasNextEpisode,
    hasPrevEpisode,
    audioElement: audioRef.current,
  };

  return (
    <RadioPlaybackContext.Provider value={contextValue}>
      {children}
    </RadioPlaybackContext.Provider>
  );
}

export function useRadioPlaybackContext() {
  const context = useContext(RadioPlaybackContext);
  if (!context) {
    throw new Error('useRadioPlaybackContext must be used within a RadioPlaybackProvider');
  }
  return context;
}
